import { RefObject, useCallback, useRef, useState } from "react";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import analytics from "web/script/analytics/analytics";

interface UseTheEditAnalyticsProps {
    label: string;
    editorialId?: number;
    editorialSlug?: string;
    isSponsored?: boolean;
    franchise?: string;
    position?: number;
    visibleThreshold?: number;
    gender?: string;
}

interface UseTheEditAnalyticsResults {
    containerRef: RefObject<HTMLDivElement>;
    sendClickEvent: (action: string) => void;
}

export function useTheEditAnalytics(params: UseTheEditAnalyticsProps): UseTheEditAnalyticsResults {
    const { label, editorialSlug, isSponsored, position, franchise, visibleThreshold, gender } =
        params;

    const containerRef = useRef<HTMLDivElement>(null);
    const { isAboveFold, isVisible } = useAboveFold(containerRef, visibleThreshold || 0.5);
    const [hasImpression, setHasImpression] = useState(false);

    const sendAnalyticsEvent = useCallback(
        (action: string, area?: string) => {
            analytics.event({
                category: "the_edit",
                action,
                label,
                nonInteraction: action !== "clicked",
                itemData: [
                    {
                        above_fold: isAboveFold,
                        ...(editorialSlug && { editorial_slug: editorialSlug }),
                        ...(position && { position: position }),
                        ...(typeof isSponsored === "boolean" && { sponsored: isSponsored }),
                        ...(area && { area: area }),
                        ...(franchise && { franchise: franchise }),
                        ...(gender && { editorial_gender: gender }),
                    },
                ],
            });
        },
        [label, isAboveFold, editorialSlug, position, isSponsored, franchise, gender]
    );

    function sendClickEvent(area: string): void {
        sendAnalyticsEvent("clicked", area);
    }

    useIsomorphicLayoutEffect(() => {
        if (isVisible && !hasImpression) {
            sendAnalyticsEvent("viewed");
            setHasImpression(true);
        }
    }, [isVisible, hasImpression, sendAnalyticsEvent, setHasImpression]);

    return { containerRef, sendClickEvent };
}
